import { Field, ErrorMessage } from 'formik';
import { useRef } from 'react';
import { Camera } from 'lucide-react';

const FilePicker = ({ name, placeholder, hint = '' }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Field name={name}>
      {({ field, form: { touched, errors, setFieldValue, setFieldTouched } }) => {
        const handleFiles = (files: FileList | null) => {
          if (files && files[0]) {
            setFieldTouched(name, true);
            setFieldValue(name, files[0]);
          }
        };

        const handleDrop = (e: React.DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          handleFiles(e.dataTransfer.files);
        };

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          e.preventDefault();
          handleFiles(e.target.files);
        };

        const selectedFile = field.value;
        const hasError = touched[name] && errors[name];
        const isFile = selectedFile instanceof File;
        const previewUrl = isFile ? URL.createObjectURL(selectedFile) : (selectedFile || null);

        return (
          <div className="form-control w-full mb-4">
            <div
              className={`
                flex flex-col items-center justify-center w-full min-h-48 p-4 overflow-hidden
                bg-slate-50 border-2 border-dashed border-secondary-200 rounded-box
                cursor-pointer transition-all duration-200 hover:border-blue-400
                ${hasError ? 'border-error bg-error/5' : ''}
              `}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
            >
              <input
                ref={inputRef}
                type="file"
                className="hidden"
                onChange={handleChange}
                accept="image/*"
              />

              {selectedFile && previewUrl ?
                <div className="relative w-full h-full flex flex-col items-center justify-center gap-2">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-h-40 object-contain rounded-md"
                  />
                </div>
                :
                <div className="flex flex-col items-center gap-3 text-secondary-400">
                  <Camera className="w-6 h-6" />
                  <p className="text-sm font-semibold text-secondary-600">{placeholder}</p>
                  {hint && <p className="text-xs">{hint}</p>}
                </div>
              }
            </div>

            <ErrorMessage name={field.name} render={(errorMsg) => (
              <label className="label pt-1">
                <span className="label-text-alt text-error">{errorMsg}</span>
              </label>
            )} />
          </div>
        );
      }}
    </Field>
  );
}

export default FilePicker;
